import { useEffect, useMemo } from 'react';
import TagManager from 'react-gtm-module';
import { useAnalytics } from '@/shell/Analytics';
import { useMatch, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text, useMantineTheme } from '@mantine/core';
import { Check } from 'react-feather';
import type { LayerConfig } from '@/views/map/layers/types';
import type { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { analysisView } from '@/app/analysis';
import {
  buildObjectiveOptions,
  type ObjectiveOption,
} from './objectiveOptionsHelpers';
import { useUserClientContext } from '@/app/clientContext';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { LISTS_DETAIL_PATH } from '@/app/constants';
import { useFetchFieldsQuery } from '@/features/fieldSlice';
import {
  getGoogleEventForAnalysis,
  GoogleEvents,
} from '@/features/googleEvents';
import { updateSelectedNetworkGroup } from '@/features/analysisMapSlice';
import { ANALYSIS_URL_PARAM } from '@/hooks/useAnalysisParam';
import { resetToAnalysisDefault } from '@/features/mapLayerSlice';
import { useFetchNetworkGroupsQuery } from '@/features/networkGroupSlice';
import { isPge } from '@/features/utils';
import { useWildfireMode } from '@/hooks/useWildfireMode';
import { useShowClearSpanAnalysis } from '@/hooks/useShowClearSpanAnalysis';
import { useDatasets } from '@/hooks/useDatasets';
import {
  getNetworkGroupLabel,
  getGroupableGroups,
  UNGROUPED,
} from '@/helpers/networkGroups';
import { LayerType } from '@/views/map/layers/types';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { SelectComponent, SelectItem } from '@/components';
import { Icon } from '@/components/Icon/Icon';
import classes from './ObjectiveDropdown.module.css';

interface ObjectiveDropdownProps {
  layerConfig: Record<LayerType, LayerConfig>;
  /** Show the group-by segment (full network view only) */
  showGroupBy: boolean;
  /** Compact mode: natural width instead of full-width on mobile */
  compact?: boolean;
}

interface ObjectiveDropdownItemProps {
  option: ObjectiveOption;
  checked: boolean;
  'data-select-testid'?: string;
}

const ObjectiveDropdownItem = ({
  option,
  checked,
  'data-select-testid': selectTestId,
}: ObjectiveDropdownItemProps) => {
  const theme = useMantineTheme();
  const intl = useClientTerminology();

  return (
    <Flex w="100%" align="center" gap={8} data-select-testid={selectTestId}>
      {/* 24px icon slot — checkmark when selected, objective icon otherwise */}
      <Flex w={24} style={{ flexShrink: 0 }} align="center" justify="center">
        {checked ? (
          <Icon
            Component={Check}
            size={16}
            color={theme.other.mainColor.accent.normal}
          />
        ) : (
          option.icon && <Icon name={option.icon} size={16} />
        )}
      </Flex>

      {/* Label + description */}
      <Box flex={1} miw={0}>
        <Text
          style={{
            ...theme.other.typography.variants.bodySmallRegular,
            color: theme.other.mainColor.foreground.primary,
            lineHeight: '20px',
          }}
        >
          {option.label}
        </Text>
        {option.description && (
          <Text
            fz="xs"
            lh="xs"
            style={{ color: theme.other.mainColor.foreground.secondary }}
          >
            {option.description}
          </Text>
        )}
      </Box>

      {/* Right: info link always shown when url exists */}
      <Flex style={{ flexShrink: 0 }} align="center">
        {option.url && (
          <a
            href={option.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={classes.infoLink}
            aria-label={intl.formatMessage({ id: 'list.learnMore' })}
          >
            <Icon name="InfoOutline" size={16} />
          </a>
        )}
      </Flex>
    </Flex>
  );
};

export const ObjectiveDropdown = ({
  layerConfig,
  showGroupBy,
  compact,
}: ObjectiveDropdownProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  const intl = useClientTerminology();
  const { context } = useUserClientContext();
  const { datasets } = useDatasets();
  const { selectedAnalysis, selectedNetworkGroup } = useAppSelector(
    (s) => s.analysisMap
  );
  const [, setSearchParams] = useSearchParams();
  const isListDetailPage = useMatch(LISTS_DETAIL_PATH);

  // --- Objective (analysis) segment ---

  const { data: fields } = useFetchFieldsQuery();

  const hasPriority =
    fields !== undefined &&
    fields.some((i) => i.field.includes('priorityRating')) &&
    !isPge(context);

  const wildfireMode = useWildfireMode();
  const showClearSpanAnalysis = useShowClearSpanAnalysis();

  const enablementOptions = useMemo(
    () => ({
      hasPriority,
      wildfireMode,
      showClearSpanAnalysis,
      datasets,
    }),
    [hasPriority, wildfireMode, showClearSpanAnalysis, datasets]
  );

  const objectiveOptions = useMemo(
    () => buildObjectiveOptions(context, enablementOptions, intl),
    [context, enablementOptions, intl]
  );

  const selectedObjectiveIcon = analysisView[selectedAnalysis]?.icon;

  // On mount and whenever selectedAnalysis changes, apply the default layer configuration
  // for the newly selected analysis.
  //
  // layerConfig and dispatch are intentionally omitted from the dependency array:
  // this effect must only fire on selectedAnalysis changes, not on every layer config update.
  useEffect(() => {
    const layerConfigForLabels = Object.prototype.hasOwnProperty.call(
      layerConfig,
      LayerType.base
    )
      ? { [LayerType.base]: layerConfig[LayerType.base] }
      : undefined;
    dispatch(resetToAnalysisDefault(selectedAnalysis, layerConfigForLabels));
  }, [selectedAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleObjectiveChange = (s: GroupAnalysisType) => {
    if (isListDetailPage) {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.projects.engagement.analysisChange,
        },
      });
    }
    setSearchParams(
      (prev) => {
        prev.set(ANALYSIS_URL_PARAM, s);
        return prev;
      },
      { replace: true }
    );

    TagManager.dataLayer({
      dataLayer: {
        event: getGoogleEventForAnalysis(s, GoogleEvents.analysis, true),
      },
    });
    track('analysis_changed', { analysis: s });
    // resetToAnalysisDefault is handled by the useEffect above on selectedAnalysis change
  };

  // --- Group-by segment ---

  const {
    data: networkGroups,
    isLoading: isNetworkGroupsLoading,
    isError: isNetworkGroupsError,
  } = useFetchNetworkGroupsQuery();

  const groupByOptions = useMemo(() => {
    if (!networkGroups) return [];

    const groupable = getGroupableGroups(networkGroups);
    const options = groupable.map((ng) => {
      const displayName = getNetworkGroupLabel(ng, context, intl);
      return {
        label: intl.formatMessage(
          { id: 'list.groupByName' },
          { name: displayName.toLowerCase() }
        ),
        value: ng.id.toString(),
        name: ng.name,
        displayName,
      };
    });

    // The ungrouped (span) option sits at the bottom; synthesize it when the catalog omits level3.
    const ungroupedLabel = getNetworkGroupLabel(UNGROUPED, context, intl);
    const level3 = options.find((o) => o.name === 'level3') ?? {
      label: intl.formatMessage(
        { id: 'list.groupByName' },
        { name: ungroupedLabel.toLowerCase() }
      ),
      value: UNGROUPED.id.toString(),
      name: UNGROUPED.name,
      displayName: ungroupedLabel,
    };
    return [...options.filter((o) => o.name !== 'level3'), level3];
  }, [networkGroups, context, intl]);

  const handleGroupByChange = (networkGroupId: string) => {
    const selected =
      networkGroups?.find((g) => g.id.toString() === networkGroupId) ??
      (networkGroupId === UNGROUPED.id.toString() ? UNGROUPED : undefined);
    if (!selected) return;
    dispatch(updateSelectedNetworkGroup(selected));

    TagManager.dataLayer({
      dataLayer: {
        event: `${GoogleEvents.groupBy}_${selected.name}`,
      },
    });
    track('group_by', { group: selected.name });
  };

  return (
    <div
      className={`${classes.container} ${compact ? classes.compact : ''}`}
      data-testid="objective-dropdown"
      data-compact={compact ? 'true' : undefined}
    >
      <div className={`${classes.segment} ${classes.objectiveSegment}`}>
        <SelectComponent
          data-testid="analysis-select"
          options={[
            {
              group: intl
                .formatMessage({ id: 'list.chooseObjective' })
                .toUpperCase(),
              items: objectiveOptions,
            },
          ]}
          selected={selectedAnalysis}
          handleChange={handleObjectiveChange}
          allowDeselect={false}
          leftSection={
            selectedObjectiveIcon ? (
              <Icon name={selectedObjectiveIcon} size={16} />
            ) : undefined
          }
          renderOption={({ option, checked }) => (
            <ObjectiveDropdownItem
              option={option}
              checked={!!checked}
              data-select-testid="analysis-select"
            />
          )}
          classNames={{ dropdown: classes.objectiveDropdown }}
          maxDropdownHeight={9999}
          comboboxProps={{ position: 'bottom-start' }}
        />
      </div>

      {showGroupBy && !isNetworkGroupsLoading && !isNetworkGroupsError && (
        <div className={classes.segment}>
          <SelectComponent
            data-testid="network-group-select"
            options={[
              {
                group: intl
                  .formatMessage({ id: 'list.groupByLabel' })
                  .toUpperCase(),
                items: groupByOptions,
              },
            ]}
            selected={selectedNetworkGroup?.id?.toString() ?? ''}
            handleChange={handleGroupByChange}
            allowDeselect={false}
            renderOption={({ option, checked }) => (
              <SelectItem
                id={option.value}
                value={option.value}
                label={option.displayName}
                selected={checked ?? false}
                data-select-testid="network-group-select"
              />
            )}
            classNames={{ dropdown: classes.groupByDropdown }}
            maxDropdownHeight={350}
            scrollAreaProps={{ type: 'auto' }}
          />
        </div>
      )}
    </div>
  );
};
